import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap";

import { adminPaths } from "admin/paths";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { appConfigRepo, paymentPlansRepo, travelCmsRepo } from "data/repositories/mobileCmsRepo";
import { confirmAdminAction } from "admin/contentUtils";

const TRAVEL_TABS = [
  { key: "flights", label: "Uçuşlar", repo: travelCmsRepo.flights, titleKey: "airline" },
  { key: "buses", label: "Otobüsler", repo: travelCmsRepo.buses, titleKey: "company" },
  { key: "hotels", label: "Oteller", repo: travelCmsRepo.hotels, titleKey: "name" },
  { key: "tours", label: "Turlar", repo: travelCmsRepo.tours, titleKey: "title" },
  { key: "cars", label: "Araçlar", repo: travelCmsRepo.cars, titleKey: "name" },
];

export default function MobileCmsPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState("plans");
  const [travelTab, setTravelTab] = useState("flights");
  const [items, setItems] = useState([]);
  const [configText, setConfigText] = useState("[]");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  const activeTravel = useMemo(
    () => TRAVEL_TABS.find((tab) => tab.key === travelTab) || TRAVEL_TABS[0],
    [travelTab]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    try {
      if (section === "plans") {
        setItems(paymentPlansRepo.list({ includeDeleted: true }));
      } else if (section === "travel") {
        setItems(activeTravel.repo.list({ includeDeleted: true }));
      } else {
        const mobile = appConfigRepo.getMobile();
        setConfigText(JSON.stringify(mobile?.featuredProvinceOrder || [], null, 2));
        setItems(appConfigRepo.list());
      }
    } catch (loadError) {
      setError(loadError?.message || "İçerik yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [activeTravel, section]);

  useEffect(() => {
    load();
  }, [load]);

  const onArchive = (item) => {
    if (!confirmAdminAction(item.title || item.name || item.airline || item.company || item.id)) return;
    try {
      if (section === "plans") paymentPlansRepo.remove(item.id);
      else if (section === "travel") activeTravel.repo.remove(item.id);
      setFlash("Kayıt arşivlendi.");
      load();
    } catch (archiveError) {
      setError(archiveError?.message || "Silinemedi");
    }
  };

  const onSaveConfig = () => {
    try {
      const parsed = JSON.parse(configText);
      if (!Array.isArray(parsed)) throw new Error("Öne çıkan şehir listesi bir dizi olmalı");
      appConfigRepo.saveMobile({ featuredProvinceOrder: parsed.map(String) });
      setFlash("Uygulama ayarları kaydedildi.");
      load();
    } catch (saveError) {
      setError(saveError?.message || "Ayarlar kaydedilemedi");
    }
  };

  return (
    <Container className="mt-4" fluid>
      <AdminPageHeader
        title="Mobil CMS"
        subtitle="Ödeme planları, seyahat sonuçları ve uygulama ayarları Firebase üzerinden yönetilir."
      />

      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink href="#plans" active={section === "plans"} onClick={(e) => { e.preventDefault(); setSection("plans"); }}>
            Ödeme Planları
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink href="#travel" active={section === "travel"} onClick={(e) => { e.preventDefault(); setSection("travel"); }}>
            Seyahat Sonuçları
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink href="#config" active={section === "config"} onClick={(e) => { e.preventDefault(); setSection("config"); }}>
            Uygulama Ayarları
          </NavLink>
        </NavItem>
      </Nav>

      {flash ? <div className="alert alert-success">{flash}</div> : null}
      <AdminStateBlock loading={loading} error={error} empty={!loading && section !== "config" && !items.length} />

      {section === "travel" ? (
        <Nav pills className="mb-3">
          {TRAVEL_TABS.map((tab) => (
            <NavItem key={tab.key}>
              <NavLink
                href={`#${tab.key}`}
                active={travelTab === tab.key}
                onClick={(e) => {
                  e.preventDefault();
                  setTravelTab(tab.key);
                }}
              >
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      ) : null}

      {section === "config" ? (
        <Card>
          <CardHeader>
            <h3 className="mb-0">Öne çıkan şehir sırası (appConfig/mobile)</h3>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Label>Province ID listesi (JSON dizi)</Label>
              <Input type="textarea" rows={10} value={configText} onChange={(e) => setConfigText(e.target.value)} />
            </FormGroup>
            <Button color="primary" onClick={onSaveConfig}>
              Kaydet
            </Button>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">
              {section === "plans" ? "Ödeme planları" : activeTravel.label}
            </h3>
            <Button
              color="primary"
              size="sm"
              onClick={() =>
                navigate(
                  section === "plans"
                    ? adminPaths.content.mobileCms.planCreate
                    : adminPaths.content.mobileCms.travelCreate(travelTab)
                )
              }
            >
              Yeni ekle
            </Button>
          </CardHeader>
          <CardBody>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Başlık</th>
                  <th>Durum</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const title =
                    item.title ||
                    item.name ||
                    item.airline ||
                    item.company ||
                    item.id;
                  return (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{title}</td>
                      <td>
                        {item.deletedAt ? (
                          <Badge color="secondary">Arşiv</Badge>
                        ) : (
                          <Badge color="success">Aktif</Badge>
                        )}
                      </td>
                      <td className="text-right">
                        <Button
                          color="link"
                          size="sm"
                          tag={Link}
                          to={
                            section === "plans"
                              ? adminPaths.content.mobileCms.planEdit(item.id)
                              : adminPaths.content.mobileCms.travelEdit(travelTab, item.id)
                          }
                        >
                          Düzenle
                        </Button>
                        {!item.deletedAt ? (
                          <Button color="link" size="sm" className="text-danger" onClick={() => onArchive(item)}>
                            Arşivle
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      <Row className="mt-3">
        <Col>
          <small className="text-muted">
            Değişiklikler Firebase Firestore koleksiyonlarına yazılır ve mobil uygulama bu verileri dinamik okur.
          </small>
        </Col>
      </Row>
    </Container>
  );
}
